import React, { useState, useEffect } from 'react';
import { Eye, Users, Send, RefreshCw, XCircle } from 'lucide-react';
import { apiService, smartUpload } from '../api/apiclient';
import MediaUploader from './MediaUploader';
import AccountSelector from '../components/AccountSelector';
import { useParams } from 'react-router-dom';
import PublishOverlay from '../components/PublishOverlay';
import { globalStore, PLATFORMS_CONFIG } from '../configs/constant';
import ConflictResolutionModal from "../components/ConflictResolutionModal";
import { useNavigate } from 'react-router-dom';
import { getThumbnailUrl, getVideoThumbnailUrl, generateThumbnail, getBaseName } from '../utils/getThumbnail';
import TikTokPostPreview from "../components/TiktokPreview";
import YoutubeSettings from "../components/YoutubeSettings";
import TagEditor from "../components/TagEditor";
import { compressMultipleImages } from '../utils/image';
import { formatAndCropVideo } from '../utils/video';
import { ErrorHandler } from '../utils/errorHandler';

const EditorPage = () => {
    const [content, setContent] = useState('');
    const [media, setMedia] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [isSyncing, setIsSyncing] = useState(false);
    const { id: editId } = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [overlay, setOverlay] = useState({
        status: 'idle', // 'idle' | 'processing' | 'success' | 'error'
        type: 'publish', // 'publish' | 'draft'
        progress: 0
    });
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [conflictReport, setConflictReport] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [tiktokSettings, setTiktokSettings] = useState({});
    const [youtubeSettings, setYoutubeSettings] = useState({});
    const [tags, setTags] = useState([]);

    const [progress, setProgress] = useState(0);
    const [processing, setProcessing] = useState(false);

    const handleVideoChange = async (videoItem) => {
        if (!videoItem) return [];
        if (typeof videoItem === "string" && videoItem.startsWith("https://")) {
            return [videoItem, getThumbnailUrl(videoItem)];
        }

        try {
            setProcessing(true);
            setProgress(0);

            const rawVideoFile = (videoItem instanceof Blob || videoItem instanceof File) ? videoItem : videoItem.file;

            if (!rawVideoFile) {
                console.error("No video url return:", videoItem);
                return [];
            }
            const processedBlob = await formatAndCropVideo(rawVideoFile, (p) => {
                setProgress(p);
            });
            const videoFileWithMeta = new File([processedBlob], `video_${Date.now()}.mp4`, {
                type: "video/mp4"
            });
            return await uploadVideoWithThumbnail(videoFileWithMeta);
        } catch (error) {
            console.error("Failed to process video:", error);
            const appError = ErrorHandler.handle(error);
            alert(appError.message);
        } finally {
            setProcessing(false);
        }
        return [];
    };

    async function uploadVideoWithThumbnail(videoFile) {
        const thumbBlob = await generateThumbnail(videoFile);
        const thumbFile = new File([thumbBlob], "thumbnail.jpg", {
            type: "image/jpeg",
            lastModified: Date.now()
        });

        const videoUrl = await smartUpload(videoFile, null, false);
        if (!videoUrl) return [];

        const videoBaseName = getBaseName(videoUrl);
        const thumbUrl = await smartUpload(thumbFile, `thumbnail/${videoBaseName}.jpg`, false);
        return [videoUrl, thumbUrl];
    }

    const handleImageChange = async (incomingItems) => {
        const itemsArray = Array.isArray(incomingItems) ? incomingItems : [incomingItems];
        if (itemsArray.length === 0) return [];

        try {
            setProcessing(true);
            setProgress(0);
            const rawFiles = itemsArray.map(item => item.file || item).filter(Boolean);
            const processedBlobs = await compressMultipleImages(rawFiles, (current, total, percent) => {
                setProgress(percent);
            });
            return await uploadToBackendStorage(processedBlobs);
        } catch (error) {
            console.error("Process images failed:", error);
            const appError = ErrorHandler.handle(error);
            alert(appError.message);
        } finally {
            setProcessing(false);
        }
        return [];
    };

    const handleAIMagic = async (targetType) => {
        try {
            if (targetType === 'content') {
                if (!content.trim()) {
                    alert("Content is empty");
                    return;
                }
                const response = await apiService.AIRewriteContent(content);
                if (!response.ok) {
                    throw new Error(JSON.stringify({
                        code: resData.code || 'HTTP_ERROR',
                        message: resData.error
                    }));
                }
                setContent(`${content} \n\n AI: ${res.data}`);

            } else if (targetType === 'tags') {
                if (!content.trim()) {
                    alert("Content is empty");
                    return;
                }
                const response = await apiService.AIGenerateTags(content);
                if (!response.ok) {
                    throw new Error(JSON.stringify({
                        code: resData.code || 'HTTP_ERROR',
                        message: resData.error
                    }));
                }
                setTags([...response.data, ...tags]);
            }
        } catch (error) {
            const appError = ErrorHandler.handle(rawError);
            alert(appError.message);
        }
    };

    const tiktokSettingDefault = {
        privacy_level: 'PUBLIC',
        allow_comment: true,
        allow_duet: true,
        allow_stitch: true,
        disclosure: false,
        is_your_brand: true,
        is_branded_content: false
    };

    const youtubeSettingDefault = {
        privacy: 'PUBLIC',
        title: "",
        for_kids: false
    };

    const navigate = useNavigate();
    const resetEditor = () => {
        setContent("");
        setMedia([]);
        setSelectedIds([]);
    }

    const [accounts, setAccounts] = useState(() => {
        return globalStore.accounts
    });

    const loadPostData = async (id) => {
        setIsLoading(true);
        try {
            const res = await apiService.getPostDetail(id);
            if (res.success) {
                const post = res.post;
                if (post.status === 'published') {
                    setContent(post.content);
                    setMedia(post.media_urls);
                    navigate('/editor', { replace: true });
                    alert("Published post not editable. Clone it for editing");
                    return;
                }

                setContent(post.content || '');
                setMedia(typeof post.media_urls === 'string' ? JSON.parse(post.media_urls) : post.media_urls);

                if (post.accountIds) {
                    setSelectedIds(post.accountIds);
                }
            }
        } catch (err) {
            console.error("Load Failed:", err);
            alert("Failed to load post data");
        } finally {
            setIsLoading(false);
        }
    };

    const syncAccounts = async () => {
        try {
            setIsSyncing(true);
            const response = await apiService.listAccounts();
            if (response.success) {
                setAccounts(response.accounts);
                globalStore.accounts = response.accounts;
                globalStore.isDirty = false;
            }
        } catch (err) {
            console.error("Sync failed:", err);
        } finally {
            setTimeout(() => setIsSyncing(false), 600);
        }
    };

    useEffect(() => {
        if (editId) {
            loadPostData(editId);
        }
    }, [editId]);

    useEffect(() => {
        if (accounts.length === 0) syncAccounts();
    }, []);

    useEffect(() => {
        const videoItem = media.find(m => m.type === 'video');
        if (videoItem && videoItem.incompatiblePlatforms?.length > 0) {
            setSelectedIds(prev => prev.filter(id => {
                const account = accounts.find(acc => acc.id === id);
                if (!account) return true;
                const isIncompatible = videoItem.incompatiblePlatforms.includes(account.platform);
                if (isIncompatible) {
                    console.log(`Auto-deselected ${account.platform} due to video limits.`);
                }
                return !isIncompatible;
            }));
        }
    }, [media, accounts]);

    async function uploadToBackendStorage(mediaFiles) {
        const uploadedAssets = [];
        for (const item of mediaFiles) {
            if (!item) continue;

            if (typeof item === 'string' && item.startsWith("https://")) {
                uploadedAssets.push(item);
                continue;
            }
            if (item.url && !item.file) {
                uploadedAssets.push(item.url);
                continue;
            }
            const actualFile = (item instanceof Blob || item instanceof File) ? item : item.file;

            if (actualFile) {
                const r2Url = await smartUpload(actualFile);
                uploadedAssets.push(r2Url);
            }
        }
        return uploadedAssets;
    }

    const handlePublish = async (forcedIds = null) => {
        if (selectedIds.length === 0) return alert("Select at least one account");
        const report = analyzeConflicts(content, media, selectedIds, accounts);
        const hasIssues = report.mustDeselect.length > 0 || report.canAutoAdjust.length > 0;
        if (hasIssues) {
            setConflictReport(report);
            setShowModal(true);
            return;
        }
        await publishHelper("publish");
    };

    const handleDraftSave = async () => {
        await publishHelper("draft")
    };

    const publishHelper = async (status) => {
        if (status === "publish" && selectedIds.length === 0) return alert("Select at least one account");
        if (!content && media.length === 0) return alert("Content and media is empty");
        const videoFile = media.find(m => m.type === 'video');
        const r2Urls = videoFile ? await handleVideoChange(media[0]) : await handleImageChange(media);
        const mediaType = videoFile ? "video" : (media.length > 0 ? "image" : "text");
        if (mediaType !== "text" && r2Urls.length === 0) {
            return alert("Media is invalid or not uploaded due to some reason. Resolve it and try again.");
        }

        const payload = {
            id: editId || null,
            content: content.substring(0, 1000),
            media_urls: videoFile ? [r2Urls[0]] : r2Urls,
            accountIds: selectedIds,
            platforms: getSelectedPlatforms(selectedIds),
            media_type: mediaType,
            thumbnail: r2Urls.length > 0 ? videoFile ? r2Urls[1] : getThumbnailUrl(r2Urls[0]) : "",
            status: status,
            settings: { "tiktok": tiktokSettings, "youtube": youtubeSettings },
            tags: tags,
        };

        try {
            setOverlay({ status: 'processing', type: status, progress: 0 });
            if (status === "draft") {
                const response = await apiService.saveDraft(payload);
                if (!response.success) {
                    throw new Error(JSON.stringify({
                        code: response.code || 'HTTP_ERROR',
                        message: response.error
                    }));
                }
            }
            if (status === "publish") {
                const response = await apiService.publishPost(payload);
                if (!response.success) {
                    throw new Error(JSON.stringify({
                        code: response.code || 'HTTP_ERROR',
                        message: response.error
                    }));
                }
            }
            setOverlay(prev => ({ ...prev, status: 'success' }));
        } catch (err) {
            setOverlay(prev => ({ ...prev, status: 'error' }));
            const appError = ErrorHandler.handle(err);
            alert(appError.message);
        } finally {
            //setOverlay({ status: 'idle', type: status, progress: 0 });
        }
    };

    const getSelectedPlatforms = (ids) => {
        const selectedPlatforms = accounts
            .filter(acc => ids.includes(acc.id))
            .map(acc => acc.platform);

        return [...new Set(selectedPlatforms)];
    };

    return (
        <div className="flex h-screen bg-white overflow-hidden relative">
            <div className="flex-1 flex flex-col min-w-0 border-r border-gray-100">
                <div className="flex-1 p-6 md:p-10 overflow-y-auto">
                    <div className="max-w-2xl mx-auto space-y-8">
                        <div className="flex justify-between items-center mb-2">
                            <h1 className="text-2xl font-bold text-gray-900 ">Content</h1>
                            <button
                                type="button"
                                onClick={() => handleAIMagic('content')}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 bg-grey-50 hover:bg-red-100 border border-red-200 rounded-xl transition-all duration-200 shadow-sm active:scale-95 group"
                            >
                                <span className="group-hover:animate-pulse">✨</span>
                                <span>AI Magic</span>
                            </button>
                        </div>
                        <textarea
                            value={content}
                            onChange={(e) => {
                                const combinedValue = e.target.value;
                                if (combinedValue.length > 1000) {
                                    setContent(combinedValue.substring(0, 1000));
                                } else {
                                    setContent(combinedValue);
                                }
                            }}
                            placeholder="Write something amazing..."
                            className="mb-0 w-full h-64 p-5 text-lg border border-gray-200 rounded-2xl focus:ring-4 focus:ring-red-50 focus:border-red-500 outline-none transition-all resize-none shadow-sm"
                        />
                        <div className="gap-1">
                            <span className={1000 - content.length <= 50 ? "text-red-500 font-bold animate-pulse" : "text-gray-400"}>
                                {1000 - content.length}
                            </span>
                            <span className="text-gray-300">/ 1000 chars left</span>
                        </div>
                        <div className="flex justify-between items-center mb-2">
                            <h1 className="text-2xl font-bold text-gray-900">Tags (Max 20)</h1>
                            <button
                                type="button"
                                onClick={() => handleAIMagic('tags')}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-700 bg-grey-50 hover:bg-red-100 border border-red-200 rounded-xl transition-all duration-200 shadow-sm active:scale-95 group"
                            >
                                <span className="group-hover:rotate-12 transition-transform">🏷️✨</span>
                                <span>Generate Tags</span>
                            </button>
                        </div>
                        <TagEditor
                            value={tags}
                            onChange={setTags}
                            placeholder="Add tag..."
                        />
                        <h1 className="text-2xl font-bold text-gray-900 mb-0">Media</h1>
                        <p className="mb-0">Max 9 images or 1 video (9:16, mp4/H.264, 1 minute)</p>
                        <MediaUploader media={media} setMedia={setMedia} />
                        {accounts?.map((account, index) => {
                            if (account.platform === "tiktok" && selectedIds.includes(account.id)) {
                                if (!tiktokSettings[account.id]) {
                                    tiktokSettings[account.id] = tiktokSettingDefault;
                                }
                                return (
                                    <TikTokPostPreview
                                        key={account.id}
                                        account={account}
                                        video={media[0]}
                                        content={content}
                                        settings={tiktokSettings[account.id] || tiktokSettingDefault}
                                        onSettingsChange={(newSettings) => {
                                            setTiktokSettings(prev => ({
                                                ...prev,
                                                [account.id]: newSettings
                                            }));
                                        }}
                                    />
                                );
                            }
                            return null;
                        })}
                        {accounts?.map((account, index) => {
                            if (account.platform === "youtube" && selectedIds.includes(account.id)) {
                                if (!youtubeSettings[account.id]) {
                                    youtubeSettings[account.id] = youtubeSettingDefault;
                                }
                                return <YoutubeSettings
                                    key={account.id || index}
                                    settings={youtubeSettings[account.id]}
                                    onSettingsChange={(newSettings) => {
                                        setYoutubeSettings(prev => ({
                                            ...prev,
                                            [account.id]: newSettings
                                        }));
                                    }}
                                />
                            }
                            return null;
                        })}
                    </div>
                </div>

                <div className="p-4 md:p-6 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4 bg-white z-10">
                    <div className="flex items-center gap-4 w-full md:w-auto">
                        <button
                            onClick={() => setIsDrawerOpen(true)}
                            className="lg:hidden flex-1 flex items-center justify-center gap-2 px-8 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition shadow-lg shadow-red-100"
                        >
                            <Users size={18} />
                            <span>{selectedIds.length} Accounts</span>
                        </button>

                        <span className="hidden lg:inline text-sm text-gray-400 font-medium">
                            {selectedIds.length} accounts selected
                        </span>
                    </div>

                    <div className="flex gap-3 w-full md:w-auto">
                        <button
                            onClick={handleDraftSave}
                            className="flex-1 md:flex-none flex items-center justify-center gap-2 px-8 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition shadow-lg shadow-red-100"
                        >
                            Save Draft
                        </button>
                        <button
                            onClick={() => handlePublish()}
                            className="flex-1 md:flex-none flex items-center justify-center gap-2 px-8 py-3 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition shadow-lg shadow-red-100"
                        >
                            <Send size={18} /> Publish Now
                        </button>
                    </div>
                </div>
            </div>

            <aside className={`
            ${isDrawerOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
            fixed lg:relative inset-y-0 right-0 z-[120] lg:z-0
            w-[85vw] md:w-[400px] bg-white lg:bg-gray-50/50 
            flex flex-col overflow-hidden transition-transform duration-300 ease-in-out border-l border-gray-100 shadow-2xl lg:shadow-none
        `}>
                <div className="lg:hidden p-5 flex justify-start items-center bg-white">
                    <button
                        onClick={() => setIsDrawerOpen(false)}
                        className="p-2 rounded-full hover:bg-gray-200 transition"
                    >
                        <XCircle size={20} className="text-gray-500" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 pb-4 custom-scrollbar">
                    <div className="space-y-4">
                        <div className="flex items-center justify-between px-1">
                            <h3 className="text-xl font-black text-gray-400 uppercase tracking-tighter pt-6">Your Network</h3>
                            <button
                                onClick={syncAccounts}
                                disabled={isSyncing}
                                className={`p-2 rounded-xl transition-colors ${isSyncing ? 'text-red-500 bg-red-50' : 'text-gray-400 hover:bg-gray-100'}`}
                            >
                                <RefreshCw size={14} className={isSyncing ? 'animate-spin' : ''} />
                            </button>
                        </div>
                        <AccountSelector
                            accounts={accounts}
                            selectedIds={selectedIds}
                            onToggle={(id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])}
                        />
                    </div>
                </div>
            </aside>

            {isDrawerOpen && (
                <div
                    className="lg:hidden fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[115] animate-in fade-in duration-300"
                    onClick={() => setIsDrawerOpen(false)}
                />
            )}

            <PublishOverlay
                {...overlay}
                totalAccounts={selectedIds.length}
                platforms={getSelectedPlatforms(selectedIds)}
                onHistory={() => navigate('/posts')}
                onGotIt={() => {
                    setOverlay({ status: 'idle', type: 'publish', progress: 0 });
                    if (overlay.status === 'success') resetEditor();
                }}
            />

            {isLoading && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center bg-white/40 animate-in fade-in duration-200">
                    <div className="flex flex-col items-center gap-3 p-6 bg-white rounded-2xl shadow-xl border border-gray-100">
                        <div className="w-10 h-10 border-4 border-gray-100 border-t-red-600 rounded-full animate-spin" />
                        <span className="text-sm font-bold text-gray-500 tracking-tight">Processing...</span>
                    </div>
                </div>
            )}
            {showModal && (
                <ConflictResolutionModal
                    report={conflictReport}
                    onCancel={() => setShowModal(false)}
                    onConfirm={() => {
                        const validIds = selectedIds.filter(id =>
                            !conflictReport.mustDeselect.some(bad => bad.id === id)
                        );
                        setShowModal(false);
                        handlePublish(validIds);
                    }}
                />
            )}
            {processing && (
                <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fade-in">
                    {/* 弹窗主体 - 移除点击背景关闭，强制用户等待 */}
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100 transform scale-100 transition-all text-center">

                        {/* 炫酷的齿轮/渲染魔幻动画 */}
                        <div className="relative w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                            <div className="absolute inset-0 rounded-full border-4 border-purple-100 animate-pulse"></div>
                            <div className="absolute inset-0 rounded-full border-4 border-t-purple-600 border-r-indigo-500 animate-spin"></div>
                            <span className="text-xl">🎬</span>
                        </div>

                        {/* 标题文案 */}
                        <h3 className="text-lg font-bold text-gray-950 mb-1">
                            {progress < 100 ? "Processing Media..." : "Optimizing Assets..."}
                        </h3>
                        <p className="text-xs text-gray-400 mb-6">
                            Please do not close this tab. We are formatting and syncing your content.
                        </p>

                        {/* 🧱 核心：进度条轨道 */}
                        <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden p-0.5 border border-gray-50 mb-3">
                            <div
                                className="bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-500 h-full rounded-full shadow-inner transition-all duration-300 ease-out relative"
                                style={{ width: `${progress}%` }}
                            >
                                {/* 进度条内流动的高光特效 */}
                                <div className="absolute inset-0 bg-[linear-gradient(45deg,rgba(255,255,255,0.15)_25%,transparent_25%,transparent_50%,rgba(255,255,255,0.15)_50%,rgba(255,255,255,0.15)_75%,transparent_75%,transparent)] bg-[length:1rem_1rem] animate-[shimmer_1s_infinite_linear]"></div>
                            </div>
                        </div>

                        {/* 百分比数字显示 */}
                        <div className="flex justify-between items-center px-1 text-sm font-semibold">
                            <span className="text-purple-600 animate-pulse">
                                {progress < 100 ? "Rendering..." : "Finishing up..."}
                            </span>
                            <span className="text-gray-700 bg-gray-100 px-2 py-0.5 rounded-md text-xs">
                                {progress}%
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const analyzeConflicts = (content, media, selectedIds, accounts) => {
    const report = {
        mustDeselect: [], // 彻底不支持，必须剔除的账号 ID
        canAutoAdjust: [], // 支持但需要裁剪媒体的账号 ID
        perfectMatch: [],  // 完全符合要求的账号 ID
        summary: { hasVideo: media.some(m => m.type === 'video'), mediaCount: media.length }
    };

    selectedIds.forEach(id => {
        const acc = accounts.find(a => a.id === id);
        const config = PLATFORMS_CONFIG[acc.platform];
        let reason = "";

        // --- 检查：致命冲突 (必须剔除) ---
        const videoInMedia = report.summary.hasVideo;
        if (videoInMedia && !config.supportVideo) {
            reason = "Doesn't support video";
        } else if (media.length === 0 && !config.allowPureText) {
            reason = "Media is required (No pure text)";
        }

        if (reason) {
            report.mustDeselect.push({ id, name: acc.account_name, platform: acc.platform, reason });
            return;
        }

        // --- 检查：规格冲突 (可自动裁剪) ---
        if (config.supportImage && media.length > (config.image?.count || 1)) {
            report.canAutoAdjust.push({
                id,
                name: acc.account_name,
                limit: config.image.count,
                reason: `Supports max ${config.image.count} images`
            });
        } else {
            report.perfectMatch.push(id);
        }
    });

    return report;
};

export default EditorPage;